'use client';

import { useEffect, useSyncExternalStore } from 'react';

export const OPEN_PRIVACY_SETTINGS_EVENT = 'ra-open-privacy-settings';
const CHANGE_KEY = 'ra-company-analytics-choice-change';

interface CompanyAnalyticsConsentState {
  loaded: boolean;
  accepted: boolean;
  decided: boolean;
  available: boolean;
}

const initial: CompanyAnalyticsConsentState = { loaded: false, accepted: false, decided: false, available: false };
let state = initial;
let revision = 0;
let saving = false;
let mounted = 0;
let blockedUntilChoice = false;
const listeners = new Set<() => void>();

const publish = (next: CompanyAnalyticsConsentState): void => {
  state = next;
  for (const listener of listeners) {
    listener();
  }
};

export const companyAnalyticsConsentAccepted = (): boolean => { return state.accepted; };
export const subscribeCompanyAnalyticsConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

const parse = (value: unknown): CompanyAnalyticsConsentState => {
  const body = value as Partial<CompanyAnalyticsConsentState> | null;
  if (!body || typeof body.accepted !== 'boolean' || typeof body.decided !== 'boolean' || typeof body.available !== 'boolean') {
    throw new Error('Invalid privacy response');
  }
  return { loaded: true, accepted: body.accepted, decided: body.decided, available: body.available };
};

export const refreshCompanyAnalyticsConsent = async (): Promise<void> => {
  if (saving) {
    return;
  }
  const current = ++revision;
  try {
    const response = await fetch('/api/privacy/company-analytics', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) {
      throw new Error('Privacy preference unavailable');
    }
    const next = parse(await response.json());
    if (current === revision) {
      publish({ ...next, accepted: next.accepted && !blockedUntilChoice });
    }
  } catch {
    if (current === revision) {
      publish({ ...initial, loaded: true });
    }
  }
};

/** Revocation is synchronous in memory, before its persistence request begins. */
export const saveCompanyAnalyticsConsent = async (accepted: boolean): Promise<void> => {
  const current = ++revision;
  saving = true;
  blockedUntilChoice = true;
  publish({ ...state, accepted: false });
  try {
    const response = await fetch('/api/privacy/company-analytics', {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accepted }),
    });
    if (!response.ok) {
      throw new Error('Your company analytics preference could not be saved. Please retry.');
    }
    const next = parse(await response.json());
    if (current === revision) {
      blockedUntilChoice = false;
      publish(next);
      try {
        // Notification only; no identifier or consent receipt is stored here.
        localStorage.setItem(CHANGE_KEY, String(Date.now()));
      } catch {
        // Other tabs also revalidate on focus.
      }
    }
  } finally {
    saving = false;
  }
};

const revalidate = (): void => {
  if (saving) {
    return;
  }
  publish({ ...state, accepted: false });
  void refreshCompanyAnalyticsConsent();
};
const storage = (event: StorageEvent): void => {
  if (event.key === CHANGE_KEY) {
    revalidate();
  }
};

export const useCompanyAnalyticsConsent = (): CompanyAnalyticsConsentState => {
  const consent = useSyncExternalStore(subscribeCompanyAnalyticsConsent, () => { return state; }, () => { return initial; });
  useEffect(() => {
    mounted += 1;
    if (mounted === 1) {
      revalidate();
      window.addEventListener('focus', revalidate);
      window.addEventListener('storage', storage);
    }
    return () => {
      mounted -= 1;
      if (mounted === 0) {
        window.removeEventListener('focus', revalidate);
        window.removeEventListener('storage', storage);
      }
    };
  }, []);
  return consent;
};
